import { presets, applyPreset } from './presets.js';
import { CONSTRAINTS, getColorPatternNames, getColorPattern } from './config.js';
import { CANVAS_CONSTANTS, KEYBOARD_CONSTANTS, PERFORMANCE_CONSTANTS } from './constants.js';
import { domCache } from './dom-cache.js';
import { i18n, t } from './i18n.js';

/**
 * Main UI controls manager (always-visible panel)
 */
export class UIControls {
    constructor(config, callbacks = {}) {
        this.config = config;
        this.callbacks = callbacks;
        this.panel = null;
        this.isExpanded = true; // Panel starts expanded
        this.isInitialized = false;
        this.controls = new Map();

        // Bind language change handler
        this.handleLanguageChange = this.handleLanguageChange.bind(this);

        this.initializeControls();
    }
    initializeControls() {
        this.panel = domCache.get('control-panel');
        this.createParameterControls();
        this.createExportControls();
        this.createAnimationControls();
        this.bindEventListeners();
        this.initializeExternalToggle();
        this.setupKeyboardShortcuts();
        this.loadPanelState(); // Uncommented - load saved state

        // Register for language change events
        i18n.onLanguageChange(this.handleLanguageChange);

        this.isInitialized = true;
    }

    // Panel state management (replaces overlay methods)
    expandPanel() {
        if (!this.panel) {
            return;
        }

        this.isExpanded = true;
        this.panel.classList.remove('collapsed');

        // Immediate redraw to preserve content during transition
        if (this.callbacks.onRedrawRequired) {
            this.callbacks.onRedrawRequired();
        }

        this.updateCanvasLayout();
        this.savePanelState(); // Uncommented - save state when expanded

        // Ensure toggle button is accessible
        this.ensureToggleButtonAccessible();

        if (this.callbacks.onPanelExpand) {
            this.callbacks.onPanelExpand();
        }
    }

    collapsePanel() {
        if (!this.panel) {
            return;
        }

        this.isExpanded = false;
        this.panel.classList.add('collapsed');

        // Immediate redraw to preserve content during transition
        if (this.callbacks.onRedrawRequired) {
            this.callbacks.onRedrawRequired();
        }

        this.updateCanvasLayout();
        this.savePanelState(); // Uncommented - save state when collapsed

        // Ensure toggle button remains accessible when collapsed
        this.ensureToggleButtonAccessible();

        if (this.callbacks.onPanelCollapse) {
            this.callbacks.onPanelCollapse();
        }
    }

    /**
     * Toggle panel between expanded and collapsed states
     */
    togglePanel() {
        if (this.isExpanded) {
            this.collapsePanel();
        } else {
            this.expandPanel();
        }
    }

    /**
     * Update canvas layout when panel state changes
     * This method handles canvas resizing and redraws when panel expands/collapses
     */
    updateCanvasLayout() {
        // Get canvas container element from cache with fallback
        const canvasContainer =
            domCache.getWithFallback('canvas-container', 'canvas-container') ||
            domCache.getWithFallback('canvas-container-class', '.canvas-container', false);

        if (!canvasContainer) {
            console.warn('Canvas container not found for layout update');
            return;
        }

        // Force a layout recalculation by briefly changing a style property
        // This ensures CSS transitions are properly applied
        const originalTransition = canvasContainer.style.transition;
        canvasContainer.style.transition = 'none';
        canvasContainer.offsetHeight; // Force reflow
        canvasContainer.style.transition = originalTransition;

        // Wait for CSS transitions to complete before resizing canvas
        const transitionDuration = CANVAS_CONSTANTS.ANIMATION_DELAY_MS || 300;

        // First immediate redraw to prevent content loss
        if (this.callbacks.onRedrawRequired) {
            this.callbacks.onRedrawRequired();
        }

        // Then wait for transition and do proper resize + redraw
        setTimeout(() => {
            // Dispatch resize event to trigger canvas resize handlers
            window.dispatchEvent(new Event('resize'));

            // Wait a bit more for resize to complete, then redraw again
            setTimeout(() => {
                if (this.callbacks.onRedrawRequired) {
                    this.callbacks.onRedrawRequired();
                }
            }, 50); // Short delay after resize
        }, transitionDuration);
    }

    /**
     * Initialize external toggle button for collapsed panel accessibility
     */
    initializeExternalToggle() {
        const externalToggle = domCache.getWithFallback(
            'external-panel-toggle',
            'external-panel-toggle',
            true
        );
        if (externalToggle) {
            externalToggle.addEventListener('click', event => {
                event.preventDefault();
                event.stopPropagation();
                if (!this.isExpanded) {
                    this.expandPanel();
                }
            });

            // Add keyboard support
            externalToggle.addEventListener('keydown', event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (!this.isExpanded) {
                        this.expandPanel();
                    }
                }
            });
        } else {
            console.warn('External toggle button not found in DOM');
        }
    }

    /**
     * Ensure the toggle button remains visible and accessible
     * This is critical when the panel is collapsed
     */
    ensureToggleButtonAccessible() {
        const externalToggle = domCache.getWithFallback(
            'external-panel-toggle',
            'external-panel-toggle',
            true
        );
        const internalToggle =
            domCache.getWithFallback('panel-toggle', 'panel-toggle') ||
            domCache.getWithFallback('panel-toggle-btn', '.panel-toggle-btn', false);

        if (this.isExpanded) {
            // Panel expanded: show internal toggle, hide external
            if (internalToggle) {
                internalToggle.style.opacity = '1';
                internalToggle.style.pointerEvents = 'auto';
            }
            if (externalToggle) {
                externalToggle.style.opacity = '0';
                externalToggle.style.pointerEvents = 'none';
            }
        } else {
            // Panel collapsed: hide internal toggle, show external
            if (internalToggle) {
                internalToggle.style.opacity = '0';
                internalToggle.style.pointerEvents = 'none';
            }
            if (externalToggle) {
                externalToggle.style.opacity = '1';
                externalToggle.style.pointerEvents = 'auto';
                externalToggle.style.zIndex = '200';
            }
        }
    }

    // Event listeners for always-visible panel
    bindEventListeners() {
        // Panel toggle button - use cached elements
        const toggleBtn =
            domCache.getWithFallback('panel-toggle', 'panel-toggle') ||
            domCache.getWithFallback('panel-toggle-btn', '.panel-toggle-btn', false);
        if (toggleBtn) {
            toggleBtn.addEventListener('click', event => {
                event.preventDefault();
                event.stopPropagation();
                this.togglePanel();
            });

            // Ensure it starts accessible
            this.ensureToggleButtonAccessible();
        } else {
            console.error('Panel toggle button not found during initialization!');
        }

        // Window resize handler - handles both window resize AND programmatic resize events
        window.addEventListener('resize', () => {
            this.handleResize();
        });

        // Close button
        const closeBtn = domCache.getWithFallback('close-button', '.close-button', false);
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.hidePanel());
        }

        // Animation buttons - use cache with fallback for dynamically created elements
        const playBtn = domCache.getWithFallback('play-animation', 'play-animation', true);
        if (playBtn) {
            playBtn.addEventListener('click', () => {
                if (this.callbacks.onAnimationStart) {
                    this.callbacks.onAnimationStart();
                }
            });
        } else {
            console.warn('Play animation button not found in cache during event binding');
        }

        const stopBtn = domCache.getWithFallback('stop-animation', 'stop-animation', true);
        if (stopBtn) {
            stopBtn.addEventListener('click', () => {
                if (this.callbacks.onAnimationStop) {
                    this.callbacks.onAnimationStop();
                }
            });
        } else {
            console.warn('Stop animation button not found in cache during event binding');
        }

        // Top export button - new implementation (integrated button)
        const topExportAction = document.querySelector('.top-export-action');
        if (topExportAction) {
            topExportAction.addEventListener('click', () => {
                const formatSelect = document.getElementById('top-export-format');
                const format = formatSelect ? formatSelect.value : 'png';

                if (this.callbacks.onExport) {
                    this.callbacks.onExport(format, { quality: 1.0 });
                }
            });
        } else {
            console.warn('Top export action button not found during event binding');
        }
    }

    handleResize() {
        // Update layout on window resize
        // Note: Removed the updateCanvasLayout() call to prevent infinite loop

        // Handle mobile/desktop layout switching
        // Check if mobile device
        const isMobile = window.innerWidth <= CANVAS_CONSTANTS.MOBILE_BREAKPOINT;
        if (isMobile && this.panel) {
            this.panel.classList.add('mobile-layout');
        } else if (this.panel) {
            this.panel.classList.remove('mobile-layout');
        }

        // CRITICAL: Redraw tree after any resize event
        // This handles both manual window resize and programmatic resize from panel changes
        if (this.callbacks.onRedrawRequired) {
            this.callbacks.onRedrawRequired();
        }
    }

    // Keyboard shortcuts (updated for always-visible panel)
    setupKeyboardShortcuts() {
        document.addEventListener('keydown', event => {
            // Toggle panel with Tab key (when not in input)
            if (event.code === 'Tab' && !this.isInputFocused() && event.ctrlKey) {
                event.preventDefault();
                this.togglePanel();
            }

            // Color preset shortcuts (Ctrl+Shift+Number)
            if (event.ctrlKey && event.shiftKey && !this.isInputFocused()) {
                const presetKeys = Object.keys(presets);
                switch (event.key) {
                    case KEYBOARD_CONSTANTS.PRESET_KEY_1:
                        event.preventDefault();
                        if (presetKeys[KEYBOARD_CONSTANTS.PRESET_INDEX_1]) {
                            this.loadPreset(presetKeys[KEYBOARD_CONSTANTS.PRESET_INDEX_1]);
                        }
                        break;
                    case KEYBOARD_CONSTANTS.PRESET_KEY_2:
                        event.preventDefault();
                        if (presetKeys[KEYBOARD_CONSTANTS.PRESET_INDEX_2]) {
                            this.loadPreset(presetKeys[KEYBOARD_CONSTANTS.PRESET_INDEX_2]);
                        }
                        break;
                    case KEYBOARD_CONSTANTS.PRESET_KEY_3:
                        event.preventDefault();
                        if (presetKeys[KEYBOARD_CONSTANTS.PRESET_INDEX_3]) {
                            this.loadPreset(presetKeys[KEYBOARD_CONSTANTS.PRESET_INDEX_3]);
                        }
                        break;
                    case KEYBOARD_CONSTANTS.PRESET_KEY_4:
                        event.preventDefault();
                        if (presetKeys[KEYBOARD_CONSTANTS.PRESET_INDEX_4]) {
                            this.loadPreset(presetKeys[KEYBOARD_CONSTANTS.PRESET_INDEX_4]);
                        }
                        break;
                    case KEYBOARD_CONSTANTS.PRESET_KEY_5:
                        event.preventDefault();
                        if (presetKeys[KEYBOARD_CONSTANTS.PRESET_INDEX_5]) {
                            this.loadPreset(presetKeys[KEYBOARD_CONSTANTS.PRESET_INDEX_5]);
                        }
                        break;
                    case KEYBOARD_CONSTANTS.PRESET_KEY_6:
                        event.preventDefault();
                        if (presetKeys[KEYBOARD_CONSTANTS.PRESET_INDEX_6]) {
                            this.loadPreset(presetKeys[KEYBOARD_CONSTANTS.PRESET_INDEX_6]);
                        }
                        break;
                }
            }
        });
    }

    createParameterControls() {
        const panelBody = domCache.getWithFallback('panel-body', '.panel-body', false);
        if (!panelBody) {
            return;
        }

        // Create parameters section
        const parametersSection = this.createSection(
            t('ui.sections.parameters'),
            'parameters-section'
        );

        // Triangle angle control
        const angleControl = this.createSliderControl(
            'triangleAngle',
            t('ui.controls.triangleAngle'),
            this.config.triangleAngle,
            CONSTRAINTS.MIN_ANGLE,
            CONSTRAINTS.MAX_ANGLE,
            1,
            t('units.degrees')
        );
        parametersSection.appendChild(angleControl);

        // Recursion depth control
        const depthControl = this.createSliderControl(
            'recursionDepth',
            t('ui.controls.recursionDepth'),
            this.config.recursionDepth,
            CONSTRAINTS.MIN_DEPTH,
            CONSTRAINTS.MAX_DEPTH,
            1,
            t('units.levels')
        );
        parametersSection.appendChild(depthControl); // Base square size control
        const sizeControl = this.createSliderControl(
            'baseSquareSize',
            t('ui.controls.baseSize'),
            this.config.baseSquareSize,
            CONSTRAINTS.MIN_BASE_SIZE,
            CONSTRAINTS.MAX_BASE_SIZE,
            5,
            t('units.pixels')
        );
        parametersSection.appendChild(sizeControl);

        panelBody.appendChild(parametersSection); // Create dedicated Colors & Patterns section
        const colorsSection = this.createSection(t('ui.sections.colors'), 'colors-section');

        // Color scheme dropdown (moved from createPresetControls)
        const schemeControl = this.createColorSchemeControl();
        colorsSection.appendChild(schemeControl);

        // Color pattern selection
        const patternControl = this.createColorPatternControl();
        colorsSection.appendChild(patternControl);

        // Color controls grouped together
        const colorsDiv = document.createElement('div');
        colorsDiv.className = 'color-controls';

        colorsDiv.appendChild(
            this.createColorControl('color1', t('ui.controls.primaryColor'), this.config.color1)
        );
        colorsDiv.appendChild(
            this.createColorControl('color2', t('ui.controls.secondaryColor'), this.config.color2)
        );
        colorsDiv.appendChild(
            this.createColorControl(
                'backgroundColor',
                t('ui.controls.backgroundColor'),
                this.config.backgroundColor
            )
        );

        colorsSection.appendChild(colorsDiv);

        // Border control (draw black outline)
        const borderControl = this.createToggleControl(
            'drawBorder',
            t('ui.controls.drawBorder'),
            this.config.drawBorder
        );
        colorsSection.appendChild(borderControl);

        // End with triangles control (as last parameter)
        const endWithTrianglesControl = this.createToggleControl(
            'endWithTriangles',
            t('ui.controls.endWithTriangles'),
            this.config.endWithTriangles
        );
        colorsSection.appendChild(endWithTrianglesControl);

        panelBody.appendChild(colorsSection);
    }

    createToggleControl(paramName, label, value) {
        const controlGroup = document.createElement('div');
        controlGroup.className = 'control-group toggle-control';

        const labelEl = document.createElement('label');
        labelEl.textContent = label;
        labelEl.setAttribute('for', `control-${paramName}`);

        const toggleInput = document.createElement('input');
        toggleInput.type = 'checkbox';
        toggleInput.id = `control-${paramName}`;
        toggleInput.checked = value;
        toggleInput.className = 'toggle-input';

        // Store control reference
        this.controls.set(paramName, { element: toggleInput });

        // Add event listener
        toggleInput.addEventListener('change', e => {
            const newValue = e.target.checked;
            this.updateParameter(paramName, newValue);
        });

        controlGroup.appendChild(labelEl);
        controlGroup.appendChild(toggleInput);

        return controlGroup;
    }

    createAnimationControls() {
        const panelBody = domCache.getWithFallback('panel-body', '.panel-body', false);
        if (!panelBody) {
            return;
        }

        const animationSection = this.createSection(
            t('ui.sections.animation'),
            'animation-section'
        );

        // Animation speed control
        const speedControl = this.createSliderControl(
            'animationSpeed',
            t('ui.controls.animationSpeed'),
            this.config.animationSpeed,
            CONSTRAINTS.MIN_ANIMATION_SPEED,
            CONSTRAINTS.MAX_ANIMATION_SPEED,
            0.1,
            t('units.speed')
        );
        animationSection.appendChild(speedControl);

        // Animation buttons
        const buttonGroup = document.createElement('div');
        buttonGroup.className = 'button-group';

        const playBtn = this.createButton(
            'play-animation',
            t('ui.buttons.playAnimation'),
            'primary'
        );
        const stopBtn = this.createButton(
            'stop-animation',
            t('ui.buttons.stopAnimation'),
            'secondary'
        );

        buttonGroup.appendChild(playBtn);
        buttonGroup.appendChild(stopBtn);
        animationSection.appendChild(buttonGroup);
        panelBody.appendChild(animationSection);
    }

    createExportControls() {
        const panelBody = domCache.getWithFallback('panel-body', '.panel-body', false);
        if (!panelBody) {
            return;
        }

        // Create prominent top export section
        const topExportSection = document.createElement('div');
        topExportSection.className = 'top-export-section';
        topExportSection.id = 'top-export-section';

        const exportContainer = document.createElement('div');
        exportContainer.className = 'top-export-container';

        // Create integrated button with dropdown on right
        const integratedBtn = document.createElement('div');
        integratedBtn.className = 'top-export-btn';
        integratedBtn.id = 'top-export-btn';

        // Left part: Download action button
        const downloadAction = document.createElement('button');
        downloadAction.className = 'top-export-action';
        downloadAction.type = 'button';
        downloadAction.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/>
            </svg>
            <span class="export-text">${t('ui.buttons.exportImage')}</span>
        `;

        // Right part: Format selector
        const formatSelect = document.createElement('select');
        formatSelect.id = 'top-export-format';
        formatSelect.className = 'top-export-format-select';

        const pngOption = document.createElement('option');
        pngOption.value = 'png';
        pngOption.textContent = 'PNG';
        formatSelect.appendChild(pngOption);

        const svgOption = document.createElement('option');
        svgOption.value = 'svg';
        svgOption.textContent = 'SVG';
        formatSelect.appendChild(svgOption);

        integratedBtn.appendChild(downloadAction);
        integratedBtn.appendChild(formatSelect);
        exportContainer.appendChild(integratedBtn);
        topExportSection.appendChild(exportContainer);

        // Insert at the beginning of panel body
        panelBody.insertBefore(topExportSection, panelBody.firstChild);
    }

    createSection(title, id) {
        const section = document.createElement('div');
        section.className = 'control-section';
        section.id = id;

        const header = document.createElement('h3');
        header.className = 'section-header';
        header.textContent = title;
        section.appendChild(header);

        return section;
    }

    createSliderControl(paramName, label, value, min, max, step, suffix = '') {
        const controlGroup = document.createElement('div');
        controlGroup.className = 'control-group';

        const labelEl = document.createElement('label');
        labelEl.textContent = label;
        labelEl.setAttribute('for', `control-${paramName}`);

        const valueDisplay = document.createElement('span');
        valueDisplay.className = 'value-display';
        valueDisplay.textContent = value + suffix;

        const slider = document.createElement('input');
        slider.type = 'range';
        slider.id = `control-${paramName}`;
        slider.min = min;
        slider.max = max;
        slider.step = step;
        slider.value = value;
        slider.className = 'slider-control';

        // Ensure proper behavior when at max value
        slider.setAttribute('data-min', min);
        slider.setAttribute('data-max', max);
        slider.setAttribute('data-step', step);

        // Store control reference
        this.controls.set(paramName, { element: slider, valueDisplay, suffix });

        // Add event listener
        slider.addEventListener('input', e => {
            const newValue = parseFloat(e.target.value);
            valueDisplay.textContent = newValue + suffix;
            this.updateParameter(paramName, newValue);
        });

        controlGroup.appendChild(labelEl);
        controlGroup.appendChild(valueDisplay);
        controlGroup.appendChild(slider);

        return controlGroup;
    }

    createColorControl(paramName, label, value) {
        const controlGroup = document.createElement('div');
        controlGroup.className = 'control-group color-control';

        const labelEl = document.createElement('label');
        labelEl.textContent = label;
        labelEl.setAttribute('for', `control-${paramName}`);

        const colorInput = document.createElement('input');
        colorInput.type = 'color';
        colorInput.id = `control-${paramName}`;
        colorInput.value = value;
        // Set picker button colors to current selection
        colorInput.style.backgroundColor = value;
        colorInput.style.color = value;
        colorInput.className = 'color-picker';

        const hexDisplay = document.createElement('span');
        hexDisplay.className = 'hex-display';
        hexDisplay.textContent = value;

        // Store control reference
        this.controls.set(paramName, { element: colorInput, hexDisplay });

        // Add event listener
        colorInput.addEventListener('input', e => {
            const newColor = e.target.value;
            // Update picker button colors on change
            colorInput.style.backgroundColor = newColor;
            colorInput.style.color = newColor;
            hexDisplay.textContent = newColor;
            this.updateParameter(paramName, newColor);
        });

        controlGroup.appendChild(labelEl);
        controlGroup.appendChild(colorInput);
        controlGroup.appendChild(hexDisplay);

        return controlGroup;
    }

    createColorPatternControl() {
        const controlGroup = document.createElement('div');
        controlGroup.className = 'control-group pattern-control';

        const labelEl = document.createElement('label');
        labelEl.textContent = t('ui.controls.colorPattern');
        labelEl.setAttribute('for', 'control-colorPattern');

        const select = document.createElement('select');
        select.id = 'control-colorPattern';
        select.className = 'pattern-dropdown'; // Get available patterns and populate dropdown
        const patternNames = getColorPatternNames();
        patternNames.forEach(patternName => {
            const option = document.createElement('option');
            option.value = patternName;

            // Use translation for pattern name, fallback to original name
            const translationKey = `patterns.${patternName}`;
            const translatedName = t(translationKey);
            option.textContent =
                translatedName !== translationKey
                    ? translatedName
                    : getColorPattern(patternName)?.name ||
                      patternName.charAt(0).toUpperCase() + patternName.slice(1);

            if (patternName === this.config.colorPattern) {
                option.selected = true;
            }
            select.appendChild(option);
        });

        // Store control reference
        this.controls.set('colorPattern', { element: select });

        // Add event listener
        select.addEventListener('change', e => {
            const newPattern = e.target.value;
            this.updateParameter('colorPattern', newPattern);
        });

        controlGroup.appendChild(labelEl);
        controlGroup.appendChild(select);
        return controlGroup;
    }

    createColorSchemeControl() {
        const controlGroup = document.createElement('div');
        controlGroup.className = 'control-group scheme-control';

        const labelEl = document.createElement('label');
        labelEl.textContent = t('ui.controls.colorScheme');
        labelEl.setAttribute('for', 'preset-select');

        const select = document.createElement('select');
        select.id = 'preset-select';
        select.className = 'preset-dropdown';

        // Add default option
        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.textContent = t('ui.presets.choose');
        defaultOption.disabled = true;
        defaultOption.selected = true;
        select.appendChild(defaultOption);

        // Add preset options
        Object.entries(presets).forEach(([key, preset]) => {
            const option = document.createElement('option');
            option.value = key;
            option.textContent = preset.name;
            option.title = preset.description; // Show description as tooltip
            select.appendChild(option);
        });

        // Add event listener
        select.addEventListener('change', e => {
            const selectedPreset = e.target.value;
            if (selectedPreset) {
                this.loadPreset(selectedPreset);
                // Reset to default option after applying preset
                setTimeout(() => {
                    select.selectedIndex = 0;
                }, PERFORMANCE_CONSTANTS.UI_TIMEOUT_MS);
            }
        });

        controlGroup.appendChild(labelEl);
        controlGroup.appendChild(select);

        return controlGroup;
    }

    createButton(id, text, className = '') {
        const button = document.createElement('button');
        button.id = id;
        button.textContent = text;
        button.className = `btn ${className}`;
        return button;
    } // Parameter management
    updateParameter(paramName, value) {
        this.config[paramName] = value;

        if (this.callbacks.onParameterChange) {
            this.callbacks.onParameterChange(paramName, value);
        }
    }

    updateUIControl(paramName, value) {
        const control = this.controls.get(paramName);
        if (control) {
            control.element.value = value;

            if (control.valueDisplay) {
                control.valueDisplay.textContent = value + (control.suffix || '');
            }

            if (control.hexDisplay) {
                control.hexDisplay.textContent = value;
                // Update picker button background and text color to match value
                control.element.style.backgroundColor = value;
                control.element.style.color = value;
            }
        }
    } // Preset management
    loadPreset(presetKey) {
        if (applyPreset(presetKey, this.config)) {
            // Update only color UI controls (preserve geometry parameters)
            const colorParams = ['color1', 'color2', 'backgroundColor'];
            colorParams.forEach(paramName => {
                this.updateUIControl(paramName, this.config[paramName]);
            });

            if (this.callbacks.onPresetLoad) {
                this.callbacks.onPresetLoad(presetKey, presets[presetKey]);
            }
        }
    }

    getAvailablePresets() {
        return Object.keys(presets);
    }

    getPresetData(presetKey) {
        return presets[presetKey] || null;
    }

    // Validation
    validateColorInput(hexColor) {
        return /^#[0-9A-F]{6}$/i.test(hexColor);
    }

    isInputFocused() {
        const activeElement = document.activeElement;
        return (
            activeElement &&
            (activeElement.tagName === 'INPUT' ||
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.tagName === 'SELECT' ||
                activeElement.contentEditable === 'true')
        );
    }

    // Save/load panel state to localStorage
    savePanelState() {
        try {
            localStorage.setItem('pythagoras-panel-expanded', JSON.stringify(this.isExpanded));
        } catch (error) {
            console.warn('Could not save panel state:', error);
        }
    }

    loadPanelState() {
        try {
            const saved = localStorage.getItem('pythagoras-panel-expanded');
            if (saved !== null) {
                const isExpanded = JSON.parse(saved);
                if (isExpanded !== this.isExpanded) {
                    // Apply the saved state without triggering callbacks or canvas updates
                    this.isExpanded = isExpanded;
                    if (this.panel) {
                        if (isExpanded) {
                            this.panel.classList.remove('collapsed');
                        } else {
                            this.panel.classList.add('collapsed');
                        }
                    }
                }
            } else {
                // No saved state found, already using default
            }

            // CRITICAL: Ensure toggle button is accessible after loading state
            setTimeout(() => {
                this.ensureToggleButtonAccessible();
            }, PERFORMANCE_CONSTANTS.UI_TIMEOUT_MS);
        } catch (error) {
            console.warn('Could not load panel state:', error);
        }
    }

    /**
     * Reset panel state to default (expanded)
     * Useful for debugging or user preference reset
     */
    resetPanelState() {
        try {
            localStorage.removeItem('pythagoras-panel-expanded');
            this.isExpanded = true;
            if (this.panel) {
                this.panel.classList.remove('collapsed');
            }
            this.updateCanvasLayout();
        } catch (error) {
            console.warn('Could not reset panel state:', error);
        }
    }

    /**
     * Get current panel state for debugging
     */
    getPanelState() {
        const toggleBtn =
            domCache.getWithFallback('panel-toggle', 'panel-toggle', true) ||
            domCache.getWithFallback('panel-toggle-btn', '.panel-toggle-btn', false);

        return {
            isExpanded: this.isExpanded,
            savedState: localStorage.getItem('pythagoras-panel-expanded'),
            panelHasCollapsedClass: this.panel ? this.panel.classList.contains('collapsed') : null,
            toggleButtonExists: !!toggleBtn,
            toggleButtonVisible: toggleBtn
                ? window.getComputedStyle(toggleBtn).visibility
                : 'unknown',
            toggleButtonOpacity: toggleBtn ? window.getComputedStyle(toggleBtn).opacity : 'unknown',
        };
    }

    /**
     * Update all UI text when language changes
     */
    updateLanguageTexts() {
        // Update panel title
        const panelTitle = document.querySelector('.panel-title');
        if (panelTitle) {
            panelTitle.textContent = t('ui.panel.title');
        }

        // Update toggle button aria-label
        const toggleBtn =
            domCache.getWithFallback('panel-toggle', 'panel-toggle', true) ||
            domCache.getWithFallback('panel-toggle-btn', '.panel-toggle-btn', false);
        if (toggleBtn) {
            toggleBtn.setAttribute('aria-label', t('ui.panel.toggle'));
        }

        // Update section headers
        this.updateSectionHeaders();

        // Update control labels
        this.updateControlLabels();

        // Update button texts
        this.updateButtonTexts();

        // Update help text
        this.updateHelpText();

        // Update dropdown options
        this.updateDropdownOptions();
    }

    /**
     * Update section headers
     */
    updateSectionHeaders() {
        const sections = {
            'parameters-section': 'ui.sections.parameters',
            'colors-section': 'ui.sections.colors',
            'animation-section': 'ui.sections.animation',
            'export-section': 'ui.sections.export',
        };

        Object.entries(sections).forEach(([sectionId, translationKey]) => {
            const section = document.getElementById(sectionId);
            if (section) {
                const header = section.querySelector('.section-header');
                if (header) {
                    header.textContent = t(translationKey);
                }
            }
        });
    }

    /**
     * Update control labels
     */
    updateControlLabels() {
        const labels = {
            'control-triangleAngle': 'ui.controls.triangleAngle',
            'control-recursionDepth': 'ui.controls.recursionDepth',
            'control-baseSquareSize': 'ui.controls.baseSize',
            'control-colorPattern': 'ui.controls.colorPattern',
            'preset-select': 'ui.controls.colorScheme',
            'control-animationSpeed': 'ui.controls.animationSpeed',
            'control-color1': 'ui.controls.primaryColor',
            'control-color2': 'ui.controls.secondaryColor',
            'control-backgroundColor': 'ui.controls.backgroundColor',
            'control-drawBorder': 'ui.controls.drawBorder',
            'control-endWithTriangles': 'ui.controls.endWithTriangles',
        };

        Object.entries(labels).forEach(([controlId, translationKey]) => {
            const label = document.querySelector(`label[for="${controlId}"]`);
            if (label) {
                label.textContent = t(translationKey);
            }
        });

        // Update export format label
        const formatLabel = document.querySelector('#export-section label');
        if (formatLabel) {
            formatLabel.textContent = t('ui.export.format');
        }
    }

    /**
     * Update button texts
     */
    updateButtonTexts() {
        const buttons = {
            'play-animation': 'ui.buttons.playAnimation',
            'stop-animation': 'ui.buttons.stopAnimation',
            'export-image': 'ui.buttons.exportImage',
        };

        Object.entries(buttons).forEach(([buttonId, translationKey]) => {
            const button = document.getElementById(buttonId);
            if (button) {
                button.textContent = t(translationKey);
            }
        });

        // Update top export button text
        const exportTextSpan = document.querySelector('.top-export-action .export-text');
        if (exportTextSpan) {
            exportTextSpan.textContent = t('ui.buttons.exportImage');
        }
    }

    /**
     * Update help text
     */
    updateHelpText() {
        const helpText = document.querySelector('.help-text');
        if (helpText) {
            helpText.innerHTML = `
                <p><strong>${t('ui.panel.help')}</strong></p>
                <p>• ${t('ui.help.zoom')}</p>
                <p>• ${t('ui.help.pan')}</p>
                <p>• ${t('ui.help.reset')}</p>
            `;
        }
    }

    /**
     * Update dropdown options
     */
    updateDropdownOptions() {
        // Update export format options
        const exportFormat = document.getElementById('export-format');
        if (exportFormat) {
            const pngOption = exportFormat.querySelector('option[value="png"]');
            const svgOption = exportFormat.querySelector('option[value="svg"]');

            if (pngOption) {
                pngOption.textContent = t('ui.export.png');
            }
            if (svgOption) {
                svgOption.textContent = t('ui.export.svg');
            }
        }

        // Update preset dropdown default option
        const presetSelect = document.getElementById('preset-select');
        if (presetSelect) {
            const defaultOption = presetSelect.querySelector('option[value=""]');
            if (defaultOption) {
                defaultOption.textContent = t('ui.presets.choose');
            }
        }

        // Update color pattern options
        const patternSelect = document.getElementById('control-colorPattern');
        if (patternSelect) {
            Array.from(patternSelect.options).forEach(option => {
                if (option.value) {
                    const translationKey = `patterns.${option.value}`;
                    const translatedName = t(translationKey);
                    const pattern = getColorPattern(option.value);
                    option.textContent =
                        translatedName !== translationKey
                            ? translatedName
                            : pattern?.name ||
                              option.value.charAt(0).toUpperCase() + option.value.slice(1);
                }
            });
        }
    }

    /**
     * Handle language change events
     */
    handleLanguageChange(newLang, oldLang) {
        try {
            // Update all translatable UI elements
            this.updateControlLabels();
            this.updateButtonTexts();
            this.updateHelpText();
            this.updateDropdownOptions();

            // Update control value displays with new suffixes
            this.updateControlValueDisplays();

            // Update panel title
            const panelTitle = document.querySelector('.panel-title, #panel-title');
            if (panelTitle) {
                panelTitle.textContent = t('ui.panel.title');
            }

            // Update section headers
            this.updateSectionHeaders();
        } catch (error) {
            console.error('Error updating UI controls for language change:', error);
        }
    }

    /**
     * Update control value displays with new translated suffixes
     */
    updateControlValueDisplays() {
        // Define the suffix mappings for controls that have translatable units
        const controlSuffixMappings = {
            triangleAngle: 'units.degrees',
            recursionDepth: 'units.levels',
            baseSquareSize: 'units.pixels',
            animationSpeed: 'units.speed',
        };

        // Update each control that has a translatable suffix
        Object.entries(controlSuffixMappings).forEach(([controlName, suffixKey]) => {
            const controlData = this.controls.get(controlName);
            if (controlData && controlData.valueDisplay && controlData.element) {
                // Get the new translated suffix
                const newSuffix = t(suffixKey);

                // Update the stored suffix
                controlData.suffix = newSuffix;

                // Update the displayed value with the new suffix
                const currentValue = controlData.element.value;
                controlData.valueDisplay.textContent = currentValue + newSuffix;
            }
        });
    }
}
